/**
 * Globale Takte, Größen und Grenzen.
 *
 * Anders als die Schwellwerte in thresholds.ts sind diese Werte nicht je
 * Ausfahrt einstellbar: Sie beschreiben, wie Client und Server miteinander
 * reden, nicht wie eine Kolonne beurteilt wird.
 *
 * Auch hier gilt: eine Zahl mit fachlicher Bedeutung gehört hierher und
 * nirgendwo sonst hin.
 */
/** Wege der Schnittstelle. Client und Server benutzen dieselben Angaben. */
export const ENDPOINTS = Object.freeze({
    apiPrefix: '/api',
    webSocketPath: '/ws',
});
export const TIMING = Object.freeze({
    /**
     * Wie lange die Ausfahrt eines Simulatorlaufs stehen bleibt.
     *
     * Sie wird nicht anonymisiert, sondern restlos gelöscht — es gibt nichts
     * daran zu schützen und nichts aufzuheben. Zwei Stunden, damit man sich das
     * Ergebnis eines Laufs noch in der Oberfläche ansehen kann.
     *
     * Ohne diese Frist wächst der Bestand mit jedem Abnahmelauf: Nach zwei Tagen
     * Entwicklung standen 26 Simulatorausfahrten mit 16.000 Positionen in der
     * Datenbank, und die echten Ausfahrten waren darin nicht mehr zu finden.
     */
    simulationRetentionMs: 2 * 60 * 60 * 1_000,
    /**
     * Nach welcher Untätigkeit eine Ausfahrt von selbst als beendet gilt.
     *
     * Das Netz unter dem Knopf „Ausfahrt beenden". Auf niemanden ist Verlass,
     * dass er am Ende noch etwas drückt — schon gar nicht auf jemanden, der froh
     * ist, angekommen zu sein. Ohne dieses Netz stünde `purge_after` nie, und
     * damit liefe die 24-Stunden-Frist aus Abschnitt 11 des Leitfadens nie an.
     *
     * Zwölf Stunden: lang genug, dass keine Mittagsrast und keine Besichtigung
     * eine laufende Ausfahrt beendet, kurz genug, dass sie am Morgen nach der
     * Fahrt beendet ist. Die Nachtpause einer Mehrtagestour läuft ausdrücklich
     * **nicht** hierüber, sondern über den Zustand `paused` — der ist von dieser
     * Regel ausgenommen.
     *
     * Das Ende wird auf die **letzte Position** datiert, nicht auf den Zeitpunkt
     * der Feststellung. Sonst verlängerte die Wartezeit die Aufbewahrung um genau
     * diese zwölf Stunden.
     */
    tourIdleFinishMs: 12 * 60 * 60 * 1_000,
    /** Sendetakt des Clients in Bewegung. */
    positionIntervalMovingMs: 1_000,
    /** Sendetakt des Clients im Stillstand. */
    positionIntervalIdleMs: 10_000,
    /**
     * Ab welcher Lücke zwischen zwei Messungen der Fahrtbericht sie nennt — und
     * nur, wenn das Fahrzeug dazwischen gefahren ist (`movingSpeedMps`).
     *
     * Zwei Takte im Stillstand. Eingemessen an zwei echten Fahrten: Mit zehn
     * Sekunden stand die Ausfahrt 450116 voller Scheinlücken — dreizehn Meter
     * GPS-Zittern in elf, zwölf Sekunden sehen nach Fahrt aus. Mit zwanzig blieben
     * bei 555769 genau die beiden Funklöcher übrig, 41 und 47 Sekunden, und bei
     * 450116 die eine Stunde, in der die App weg war, während der Wagen fuhr.
     *
     * Gebraucht, um A-137 auf der Straße zu bestätigen: Beim Hineinfahren ins
     * Funkloch gingen vorher rund vierzig Sekunden Messungen verloren.
     */
    reportGapMs: 20_000,
    /**
     * Ab dieser Geschwindigkeit gilt ein Fahrzeug als in Bewegung. Darunter, und
     * bei geringer Positionsänderung, schaltet der Client auf den langsamen Takt.
     * 0,5 m/s sind knapp 2 km/h — langsamer als Schritttempo.
     */
    movingSpeedMps: 0.5,
    /**
     * Entfernung von der Stelle, an der ein Fahrzeug zuletzt stand, ab der es
     * wieder als fahrend gilt.
     *
     * Nicht die Strecke zwischen zwei Meldungen: Ein stehendes Gerät wandert um
     * ein, zwei Meter hin und her und kommt doch nirgends an.
     */
    movingDistanceM: 15,
    /**
     * So lange muss ein Fahrzeug in der Nähe derselben Stelle bleiben, bevor es
     * als stehend gilt.
     *
     * Ohne diese Wartezeit fiele ein fahrendes Fahrzeug zwischen zwei Meldungen
     * immer wieder kurz auf „steht" zurück — es ist ja nie mehr als eine
     * Wagenlänge von der Stelle entfernt, an der es eine Sekunde zuvor war. Fünf
     * Sekunden trennen das eine vom anderen und liegen weit unter der Zeit, nach
     * der Stillstand gemeldet wird.
     */
    standingConfirmMs: 5_000,
    /**
     * Serverseitige Bündelung: jeder Client erhält Positionen höchstens einmal je
     * Sekunde, gesammelt für alle Fahrzeuge.
     */
    broadcastIntervalMs: 1_000,
    /**
     * Wie lange nach einem misslungenen Versuch die Bildschirmsperre ein zweites
     * Mal angefordert wird.
     *
     * Ein einzelner Versuch beim Start reicht nicht: Schlägt er fehl, weil die
     * Seite in dem Augenblick noch nicht vollständig da war, bliebe der
     * Bildschirm für die ganze Fahrt ungesperrt — und mit ihm endete die
     * Positionsermittlung, sobald er ausgeht.
     *
     * Genau **ein** zweiter Versuch, und der nach vier Sekunden: Lehnt der
     * Browser dauerhaft ab — im Stromsparmodus tut er das —, ändert kein
     * weiterer Versuch daran etwas, und ein Takt, der ins Leere läuft, kostet
     * nur Strom.
     */
    wakeLockRetryMs: 4_000,
    /** Heartbeat des Clients. */
    /**
     * Wie lange der Client nach dem Öffnen auf die **erste Antwort** des Servers
     * wartet, bevor er die Verbindung für tot hält.
     *
     * Bis zum 09.09.2026 galt eine Verbindung als offen, sobald der Socket stand
     * — die Anmeldung wurde abgeschickt und nicht mehr nachgehalten. Ging sie
     * unterwegs verloren, zeigte die App „verbunden" und sendete ins Leere; das
     * Einzige, was sie herausholte, war die Absage des Servers nach zehn
     * Sekunden. Ohne die hätte es bis zum Herzschlag-Zeitablauf gedauert (A-107).
     *
     * Bewusst länger als die Anmeldefrist des Servers: Kommt dessen Absage, ist
     * sie die bessere Auskunft und soll zuerst greifen. Und bewusst großzügig,
     * denn die erste Antwort ist die größte — sie trägt Teilnehmer, Meldungen und
     * die Führungsspur. Auf einer schmalen Leitung darf das dauern, ohne dass
     * eine tragende Verbindung abgeschossen wird.
     */
    joinConfirmTimeoutMs: 20_000,
    heartbeatIntervalMs: 15_000,
    /**
     * Ohne Antwort auf den Heartbeat gilt die Verbindung als tot und wird
     * geschlossen. Ob ein Teilnehmer dann „kein Signal" hat, entscheidet
     * getrennt davon noSignalTimeoutS aus den Schwellwerten der Ausfahrt.
     */
    heartbeatTimeoutMs: 45_000,
    /** Wiederverbinden mit ansteigender Wartezeit. */
    reconnectBaseMs: 1_000,
    reconnectMaxMs: 30_000,
    reconnectFactor: 2,
    /** Zufällige Streuung, damit nicht alle Fahrzeuge gleichzeitig wiederkommen. */
    reconnectJitterMs: 500,
});
/**
 * Maße und Zeiten der Hülle.
 *
 * Nicht fachlich, sondern der Umgang mit einer Eigenheit der Geräte — sie
 * stehen trotzdem hier, weil eine Zahl mit Bedeutung nirgendwo sonst stehen
 * soll (Abschnitt 7 des Leitfadens).
 */
export const LAYOUT = Object.freeze({
    /**
     * Wie viele fehlende Fahrzeuge am Treffpunkt namentlich dastehen.
     *
     * Zwölf Kennzeichen untereinander schieben die Karte aus dem Bild, und wer
     * zwölf vermisst, zählt ohnehin nicht mehr nach — dann sagt eine Zahl mehr.
     * Vier passen auf ein Telefon in der Halterung, ohne dass jemand lesen muss.
     */
    fehlendeSichtbar: 4,
    /**
     * Wie lange nach einer Drehung noch einmal nachgemessen wird.
     *
     * iOS meldet `orientationchange`, **bevor** die neuen Maße feststehen. Wer
     * nur in diesem Augenblick misst, bekommt die Höhe der alten Lage — und
     * genau daraus entstand die weiße Fläche am unteren Rand.
     *
     * Dreihundert Millisekunden liegen hinter der Drehanimation des Geräts und
     * weit vor dem Augenblick, in dem jemand die neue Lage überhaupt bedient.
     */
    orientationSettleMs: 300,
    /**
     * Wie lange ein Hinweis stehen bleibt, der sich von selbst schließt.
     *
     * Nicht jeder Hinweis darf das. Es gilt allein für die, die bestätigen, was
     * ohnehin gut ist — „Willkommen zurück, die Standortübertragung läuft
     * wieder" und „Du bist wieder bei der Kolonne". Sie verlangen nichts vom
     * Fahrer; ihn dafür tippen zu lassen, heißt, ihn während der Fahrt für eine
     * gute Nachricht zu beschäftigen.
     *
     * Was eine Störung meldet — Routingdienst nicht erreichbar, kein Weg
     * berechenbar, aus der Kolonne genommen —, bleibt stehen, bis jemand es
     * gelesen hat. Ein Hinweis, den man übersehen kann, taugt dort nicht.
     *
     * Fünf Sekunden: lang genug, um im Vorbeischauen gelesen zu werden — der
     * Satz hat sieben Wörter —, kurz genug, dass die Fläche nicht im Weg steht.
     * Die Schaltfläche bleibt daneben; wer schneller ist, tippt.
     */
    selbstschliessendMs: 5_000,
    /**
     * Wie lange eine Fläche auf die Antwort des Servers wartet, bevor sie
     * aufgibt.
     *
     * Eine Rollenänderung geht über die Verbindung raus und gilt erst, wenn das
     * neue `tour_state` zurückkommt. Solange steht auf der Fläche „Einen
     * Augenblick …" — und irgendwann muss Schluss sein, sonst wartet sie bis zum
     * Ende der Fahrt.
     *
     * Vier Sekunden: Die Antwort kommt im Betrieb in Millisekunden; wer so lange
     * wartet, hat kein Netz mehr. Kürzer wäre auf einer schlechten Leitung eine
     * Fehlmeldung, länger stünde der Fahrer vor einer Fläche, die nichts sagt.
     *
     * Am 30.08.2026 auf der Ausfahrt 242008 nötig geworden: Die Fläche zeigte
     * nach dem Tippen nichts an, der Fahrer tippte ein zweites Mal — und der
     * zweite Tipp nahm die eben übernommene Rolle wieder weg. Eine Sekunde lang
     * war er Schlusswagen.
     */
    rollenwechselFristMs: 4_000,
});
export const LIMITS = Object.freeze({
    /**
     * Größe des Puffers für das Funkloch. 3600 Meldungen sind bei einer Meldung
     * je Sekunde eine Stunde ohne Verbindung; ältere werden verworfen, die
     * neuesten bleiben.
     */
    offlineBufferMaxPositions: 3_600,
    /** Höchstzahl Positionen in einer Nachsende-Nachricht. Größere Puffer werden aufgeteilt. */
    positionsPerBatch: 500,
    /** Höchstzahl Spurpunkte in einer Zuwachs-Nachricht. */
    trackPointsPerUpdate: 1_000,
    /**
     * Höchstzahl Punkte einer eingelesenen Route (Stufe 3).
     *
     * Zwanzigtausend sind bei einer Strecke von 300 km ein Punkt alle fünfzehn
     * Meter — feiner, als eine Ausfahrt je braucht, und mehr, als Komoot oder
     * Kurviger ausgeben. Die Grenze ist keine Geschmacksfrage: Die Route wird
     * **als Ganzes** übertragen, nicht als Zuwachs wie die Führungsspur, und
     * landet damit auf jedem Telefon in einer einzigen Nachricht.
     *
     * Wird sie erreicht, ist die Antwort nicht eine größere Zahl, sondern eine
     * ausgedünnte Strecke — die Punktdichte einer Aufzeichnung sagt über den
     * **Weg** nichts, sie sagt etwas über das Gerät, das sie aufgezeichnet hat.
     */
    routePointsMax: 20_000,
    /**
     * Größe einer hochgeladenen GPX-Datei in Bytes.
     *
     * Abgeleitet und nicht gegriffen: `routePointsMax` Punkte mit Höhe und
     * Zeitstempel sind rund 120 Bytes je Punkt, also etwa 2,4 MB. Acht Megabyte
     * lassen Luft für Kopfdaten, Erweiterungen und Einrückung — und bleiben
     * weit unter dem, was ein Server ohne Not im Speicher halten sollte.
     *
     * Die Datei wird **vollständig gelesen**, bevor irgendetwas geprüft wird.
     * Ohne diese Grenze entschiede die Gegenstelle, wie viel Speicher sie belegt.
     */
    routeUploadMaxBytes: 8 * 1024 * 1024,
    /**
     * Nachkommastellen der übertragenen Koordinaten. Sechs Stellen sind etwa
     * elf Zentimeter — deutlich genauer, als GPS im Auto je liefert.
     */
    coordinateDecimals: 6,
    /**
     * Wie viele eingegangene Meldungen dem Fahrer gleichzeitig vor Augen stehen.
     *
     * Melden fünf Fahrzeuge kurz nacheinander, darf die Karte nicht unter
     * Meldungen verschwinden. Die neuesten bleiben, die älteren rücken in den
     * Verlauf — sie sind dort nicht verloren, nur nicht mehr im Weg.
     */
    visibleStatusMessages: 3,
    /** Höchstlänge freier Texteingaben beim Beitritt. */
    displayNameMaxLength: 40,
    /**
     * Kennzeichen. Fünfzehn Zeichen fassen jedes deutsche Kennzeichen samt
     * Bindestrichen und Leerzeichen und lassen Raum für ausländische.
     */
    licensePlateMaxLength: 15,
    vehicleLabelMaxLength: 60,
    tourNameMaxLength: 80,
    /**
     * Name eines Treffpunkts. Er steht während der Fahrt in **einer** Zeile neben
     * der Karte — „Mittagsrast im Gasthof Bremer" passt, ein Satz nicht. Wer mehr
     * schreiben will, meint eine Beschreibung, und die gibt es hier nicht.
     */
    waypointNameMaxLength: 60,
    /**
     * Wie viele Treffpunkte eine Ausfahrt haben darf.
     *
     * Nicht aus Sparsamkeit: `route_plan` geht als Ganzes an jedes Gerät, und die
     * Liste steht während der Fahrt auf dem Bildschirm. Eine Tagesausfahrt hat
     * eine Handvoll; fünfzig wären bereits ein Zeichen dafür, dass jemand
     * Stützpunkte statt einer Route pflegt.
     */
    waypointsMax: 50,
});
/**
 * Die Ankunftszeit am nächsten Treffpunkt (Stufe 3, Schritt 2).
 *
 * ## Woher das Tempo kommt
 *
 * **Nicht aus dem Straßenprofil.** Ein Routendienst rechnet für einen modernen
 * Wagen, nicht für zwölf Oldtimer, die an jeder Ortsdurchfahrt zusammenbleiben
 * und zwischendurch auf den Nachzügler warten. Die ehrlichere Zahl steht schon
 * in der Führungsspur: `travelMs` trägt die tatsächlich gefahrene Zeit dieser
 * Kolonne an diesem Tag.
 *
 * ## Was diese Zahl nicht enthält
 *
 * `travelMs` wächst im Stillstand **nicht** — genau dafür wurde es eingeführt,
 * damit eine Ampel nicht als Rückstand zählt. Die Ankunftszeit daraus ist
 * deshalb reine **Fahrzeit** und fällt dort zu günstig aus, wo die Kolonne
 * regelmäßig hält.
 *
 * Die Alternative wäre die verstrichene Zeit nach der Wanduhr. Sie enthielte
 * die Ampeln — und ebenso die Mittagspause: Fiele die ins Messfenster, stünde
 * ein Tempo von zwei Kilometern je Stunde da und die Ankunft läge am Abend.
 * Ein Wert, der gelegentlich um zehn Prozent danebenliegt, ist einem
 * vorzuziehen, der gelegentlich um das Zehnfache danebenliegt. Die Oberfläche
 * nennt die Zahl deshalb Fahrzeit und keine Uhrzeit (A-119).
 */
export const ARRIVAL = Object.freeze({
    /**
     * Über welche Strecke das Tempo gemittelt wird.
     *
     * Fünf Kilometer sind mehr als eine Ortsdurchfahrt und weniger als ein
     * halber Tag: lang genug, dass ein einzelner Berg das Ergebnis nicht kippt,
     * kurz genug, dass die Autobahnanfahrt von heute früh nicht mehr mitzählt.
     */
    paceWindowM: 5_000,
    /**
     * So weit muss die Führung gefahren sein, bevor überhaupt ein Tempo gilt.
     *
     * Darunter steht kein Wert da statt eines geratenen. Die ersten
     * dreihundert Meter einer Ausfahrt sind Rangieren auf dem Parkplatz, und
     * daraus eine Ankunftszeit zu rechnen hieße, eine Zahl zu erfinden.
     */
    paceMinM: 300,
    /**
     * Bis hierher gilt ein Fahrzeug als **auf der Route** — und nur dann trägt
     * die Entfernung entlang der Strecke eine Auskunft.
     *
     * ## Woher die fünfzig Meter kommen
     *
     * Aus der Fahrt vom 16.09.2026, nachgespielt Position für Position. Auf der
     * Anfahrt zum Start fuhr Markus 250 bis 350 m neben der geplanten Route, und
     * dort ist das Lot darauf mehrdeutig: Es sprang um 05:24:30 in einem
     * einzigen Schritt **um 1,5 Kilometer**. Die Leiste zeigte daraufhin „noch
     * 21 m", während der Treffpunkt 356 m entfernt war.
     *
     * Gemessen wurde dabei auch, ab wann die Zahl wieder trägt:
     *
     *   neben der Route   entlang der Route   tatsächlich (Luftlinie)
     *        346 m               21 m                 356 m
     *        156 m                2 m                 169 m
     *         40 m               42 m                  51 m
     *
     * Vorher stand hier 1000 m — von mir gesetzt, ohne einen einzigen Beleg.
     *
     * Beim Fahren **auf** der Route liegt der Abstand bei rund zehn Metern; die
     * Leiste bleibt also durchgehend stehen. Wer weiter daneben ist, bekommt die
     * Luftlinie und sieht, dass es eine ist (A-125).
     */
    onRouteM: 50,
});
/**
 * Spielräume bei der Zuordnung eines Fahrzeugs zur Führungsspur.
 *
 * Die Zuordnung folgt dem bisherigen Verlauf des Fahrzeugs: gesucht wird nur
 * dort, wo es seit der letzten Zuordnung überhaupt hingekommen sein kann. Das
 * ist die Auflösung für Rundkurse — führt die Strecke ein zweites Mal über
 * dieselbe Straße, gewinnt nicht der räumlich nächste Punkt, sondern der zum
 * Verlauf passende.
 *
 * Die Spielräume fangen ab, dass GPS ungenau ist und die Spur nur alle paar
 * Meter einen Punkt hat.
 */
export const MATCHING = Object.freeze({
    /** Zusätzlicher Spielraum in Fahrtrichtung. */
    forwardSlackM: 150,
    /** Zusätzlicher Spielraum entgegen der Fahrtrichtung — jemand dreht um und fährt zurück. */
    backwardSlackM: 250,
    /**
     * Wie weit darf der Kurs des Fahrzeugs von der Richtung der Spur abweichen,
     * damit eine Vorbeifahrt als die eigene infrage kommt?
     *
     * Der Fall, für den es das gibt: eine Stichstraße. Die Kolonne fährt hinaus
     * und dieselbe Straße wieder zurück. Auf dem Rückweg liegen die eigene
     * Position und die Hinfahrt der Führung wenige Meter auseinander — räumlich
     * passt beides, und ohne diese Regel gewinnt gern die Hinfahrt. Der
     * gemeldete Rückstand ist dann die Fahrzeit zwischen beiden Vorbeifahrten.
     *
     * Genau das ist am 12.08.2026 aufgetreten: Führung und Schlusswagen saßen im
     * selben Fahrzeug, gemeldet waren 1:32 min Rückstand — die Zeit von der
     * Hinfahrt um 13:40 bis zur Rückfahrt um 13:48.
     *
     * Wer die Straße in der Gegenrichtung befährt, kommt nicht von dort, wo die
     * Führung damals herkam. Neunzig Grad wären zu scharf: In einer Kurve steht
     * der Kurs quer zum geloteten Abschnitt. Hundert Grad lassen das zu und
     * schließen die Gegenrichtung sicher aus.
     */
    courseToleranceDeg: 100,
    /**
     * Ab diesem Tempo ist der gemeldete Kurs brauchbar.
     *
     * Im Stand liefert GPS eine Richtung, die sich im Kreis dreht — sie taugt
     * dann für gar nichts. Unterhalb dieser Schwelle bleibt die Richtung
     * deshalb außer Betracht, und es entscheidet allein der bisherige Verlauf.
     */
    courseMinSpeedMps: 3,
    /**
     * Um so viel weiter darf der Kandidat aus dem Verlaufsfenster höchstens
     * entfernt liegen als der nächstgelegene überhaupt.
     *
     * Das Fenster **bevorzugt**, es **erzwingt** nicht. Ohne diese Grenze konnte
     * es die Zuordnung festhalten: Am 16.08.2026 bei Ouddorp geriet ein Fahrzeug
     * beim Wenden für einen Augenblick auf die Hinfahrt derselben Straße — im
     * Stand taugt der Kurs nicht, und räumlich liegen beide Fahrbahnen wenige
     * Meter auseinander. Von da an hielt das Fenster die Zuordnung dort fest: Der
     * richtige Punkt auf der Rückfahrt lag in der Streckenlänge über dreihundert
     * Meter weiter — mehr, als das Fenster zulässt —, und weil ein Kandidat
     * darin blieb, merkte niemand etwas. Das Fahrzeug fuhr dem festgehaltenen
     * Punkt davon, der gemeldete Abstand wuchs Sekunde um Sekunde, und bei 150 m
     * meldete der Server dem Führungsfahrzeug „Abseits der Strecke" — für ein
     * Fahrzeug, das im selben Wagen saß.
     *
     * Fünfzig Meter liegen weit über der Streuung des GPS und weit unter der
     * Schwelle, ab der ein Fahrzeug als abseits der Strecke gilt. Die Auflösung
     * von Rundkursen bleibt unberührt: Dort liegen beide Vorbeifahrten wenige
     * Meter auseinander, nicht fünfzig.
     */
    hintOverrideM: 50,
    /**
     * Um wie viel weiter der nächste **richtungsgerechte** Kandidat höchstens
     * liegen darf als der nächste überhaupt, damit der Richtungsfilter gilt.
     *
     * Der Filter wählt aus, er springt nicht. Er ist dafür da, auf einer zweimal
     * befahrenen Straße die richtige Vorbeifahrt zu treffen — dort liegen beide
     * Kandidaten wenige Meter auseinander, und die Richtung entscheidet.
     *
     * Wer aber die gefahrene Strecke **rückwärts** zurückfährt, hat in seiner
     * Nähe überhaupt keinen richtungsgerechten Kandidaten. Ohne diese Schranke
     * bleiben dann nur weit entfernte übrig, die zufällig passen — und aus einer
     * Auswahl wird ein Sprung.
     *
     * Am 27.08.2026 auf der Ausfahrt „Atteln_20260827" geschah genau das: Ein
     * Fahrzeug fuhr die Spur der Führung entgegen ihrer Richtung zurück, 1 bis 9
     * Meter neben ihr. Die Zuordnung hielt an einer Stelle fest, die bis zu **673
     * Meter** vom wirklich nächsten Punkt entfernt lag, meldete daraus einen
     * Abstand von 501 Metern — und das Führungsfahrzeug bekam
     * zweiundfünfzig Sekunden lang „Abseits" für einen Wagen auf der Strecke.
     *
     * Derselbe Gedanke und derselbe Wert wie bei `hintOverrideM`: Fünfzig Meter
     * trennen „die andere Fahrbahn" von „ein ganz anderer Ort".
     */
    courseOverrideM: 50,
    /**
     * Um wie viel die Strecke bis zur Führung die Luftlinie höchstens
     * übersteigen darf, damit der Rückstand geglaubt wird.
     *
     * Der Rückstand entsteht aus der Zuordnung zur Spur. Liegt die Zuordnung auf
     * der falschen Vorbeifahrt eines Rundkurses, ist er nicht ein bisschen
     * falsch, sondern um Größenordnungen: Am 20.08.2026 wurden **25:09**
     * gemeldet, während die Führung **81 Meter** entfernt stand — die Strecke
     * entlang der Spur wäre 257-mal so lang gewesen wie die Luftlinie.
     *
     * Zwanzig ist mit Absicht großzügig. Auf Serpentinen ist die Straße
     * tatsächlich ein Vielfaches der Luftlinie — bis etwa zum Zehnfachen, wenn
     * eine Kehre unmittelbar übereinanderliegt. Das Zwanzigfache erreicht keine
     * Straße mehr; das heißt: die Zuordnung liegt auf der falschen Vorbeifahrt.
     *
     * Gemessen an den drei Sprüngen jener Fahrt: Verhältnis 53, 257 und 557.
     */
    implausibleGapFactor: 20,
    /**
     * Unterhalb dieser Strecke wird die Plausibilität gar nicht erst geprüft.
     *
     * Bei wenigen Metern Rückstand ist jedes Verhältnis zufällig — steht ein
     * Fahrzeug zwei Meter neben der Spur und dreißig Meter dahinter, sind das
     * schon fünfzehn. Erst ab einer Strecke, die ein Fahrzeug wirklich trennt,
     * sagt der Vergleich etwas aus.
     */
    implausibleGapMinM: 300,
    /**
     * Ab dieser Nähe gelten zwei **stehende** Fahrzeuge als beieinander.
     *
     * Dreißig Meter sind eine Parkplatzbreite. Zwei Fahrzeuge, die beide stehen
     * und keine dreißig Meter auseinander sind, machen zusammen Pause — was auch
     * immer die Spur zwischen ihren Positionen behauptet.
     */
    togetherM: 30,
});
/**
 * Die Kameraführung der Karte.
 *
 * Die Karte wird Bild für Bild nachgeführt, damit die Fahrzeuge gleiten statt
 * zu springen. Der Ausschnitt für die ganze Kolonne darf dabei aber nicht in
 * jedem Bild neu berechnet werden: Jede Anpassung ist eine Kamerafahrt, und
 * sechzig gleichzeitige Kamerafahrten je Sekunde kosten Rechenzeit, ohne dass
 * man sie sieht.
 */
export const MAP_FOLLOW = Object.freeze({
    /** Takt, in dem der Ausschnitt an die ganze Kolonne angepasst wird. */
    fitIntervalMs: 700,
    /** Dauer dieser Kamerafahrt. Etwas länger als der Takt, damit sie nicht stockt. */
    fitDurationMs: 800,
    /**
     * Zeitkonstante der weichen Bewegung: So lange braucht ein Fahrzeug auf der
     * Karte, um 63 % des Weges zu seiner gemeldeten Position zurückzulegen.
     *
     * Über die **Zeit** und nicht über die Bildrate. Vorher rückte jedes Fahrzeug
     * je Einzelbild um einen festen Anteil näher; bei 60 Bildern je Sekunde war
     * das in einer knappen halben Sekunde erledigt. Nach dem Aufwachen aus dem
     * Hintergrund drosselt iOS die Bildrate jedoch für einige Augenblicke — und
     * derselbe Weg dauerte dann ein Vielfaches. Am 16.08.2026 auf dem iPad als
     * Zeitlupe beobachtet.
     */
    approachTauMs: 85,
    /**
     * Ab dieser Entfernung wird gesprungen statt geglitten — auf der Karte wie
     * mit der Kamera.
     *
     * Zwei Gründe. Der erste ist Ehrlichkeit: Wer aus dem Hintergrund zurückkommt
     * oder aus einem Funkloch nachsendet, ist die Strecke nicht gefahren; sie
     * darzustellen zeigte eine Fahrt, die nie stattgefunden hat. Der zweite ist
     * Ruhe: Über Kilometer sieht jede weiche Annäherung nach Kriechen aus.
     *
     * 200 m liegen über jeder Lücke im Fahrbetrieb — bei 100 km/h und einer
     * Meldung je Sekunde sind es 28 m — und weit unter dem, was eine Pause
     * hinterlässt.
     */
    jumpDistanceM: 200,
    /**
     * Der Ausschnitt während einer laufenden Navigation.
     *
     * Beim Navigieren sucht der Fahrer etwas anderes als in der Kolonnenansicht:
     * nicht „wo sind die anderen", sondern „was kommt gleich". Deshalb rückt sein
     * Fahrzeug nach unten ins Bild, und der Platz darüber gehört der Strecke.
     *
     * Am 26.08.2026 hatte ein Fahrer die Kolonnenansicht an, während seine
     * Navigation lief. Die Kreuzung, an der er in 500 Metern abbiegen sollte, lag
     * außerhalb des Bildes — der Ausschnitt war auf die Führung eingepasst und
     * nicht auf seinen Weg.
     *
     * Angegeben als Anteil der Kartenhöhe: 0,12 heißt, das eigene Fahrzeug steht
     * gut ein Zehntel unterhalb der Mitte — bei etwa 62 Prozent der Höhe.
     *
     * **Warum nicht weiter unten.** Die Karte reicht über den ganzen Bildschirm,
     * aber sichtbar ist sie nur oben: Gemessen an der eigenen Gestaltung beginnt
     * die Bedienung bei 82 Prozent der Höhe, und die Rückstandsfläche liegt noch
     * darüber. Ein Anteil von 0,3 setzte das Fahrzeug auf 80 Prozent — genau in
     * den verdeckten Bereich, und der Fahrer sähe sich selbst nicht mehr.
     *
     * 62 Prozent liegen sicher über beidem und lassen trotzdem knapp zwei Drittel
     * des Bildes für den Weg nach vorn.
     */
    /**
     * Wie viel Karte unter dem eigenen Fahrzeug frei bleibt, in Pixeln.
     *
     * Bei laufender Navigation gehört der Platz nach oben — dorthin, wo der Weg
     * hinführt. Das Fahrzeug steht deshalb so tief, wie es geht, ohne hinter
     * Rückstandsfläche und Bedienung zu geraten: genau diesen Abstand darüber.
     *
     * Vierundsechzig Pixel, die kleine Tippfläche. Das Sinnbild des Fahrzeugs ist
     * etwa fünfzig Pixel hoch und sitzt mittig auf dem Punkt; damit bleibt es
     * vollständig frei, und ein Stück der gerade gefahrenen Straße ist noch zu
     * sehen.
     *
     * ## Zwei Anläufe stecken darin
     *
     * Zuerst stand hier ein fester Anteil der ganzen Kartenhöhe (0,12). Der
     * setzte das Fahrzeug bei laufender Navigation auf 503 von 812 Pixeln —
     * hinter die Flächen, sichtbar war nur bis 493 (A-80).
     *
     * Dann drei Viertel des **sichtbaren** Teils: 370 Pixel. Das war frei, aber
     * **höher als ohne Navigation** — das Fadenkreuz stellt das Fahrzeug in die
     * Mitte, also auf 406. Ausgerechnet in der Ansicht, die dem Weg nach vorn
     * gehören soll, sah der Fahrer am wenigsten davon. Am 28.08.2026 auf dem
     * Gerät bemerkt.
     *
     * Jetzt ergeben dieselben Maße 429 Pixel: mehr Weg voraus als in jeder der
     * beiden Fassungen davor, und vierundsechzig Pixel Luft nach unten.
     */
    navigationBottomMarginPx: 64,
    /**
     * Wie lange „Führungsfahrzeug zeigen" während der Navigation gilt.
     *
     * Ein Blick, kein Zustand: Danach springt die Karte von selbst auf die
     * Navigationsansicht zurück. Wer die Spitze sucht, während er geführt wird,
     * will sie sehen — und dann weiterfahren, ohne noch eine Fläche zu treffen.
     */
    navigationGlanceMs: 8_000,
    /**
     * Vergrößerung nach gefahrener Geschwindigkeit — die Stützstellen.
     *
     * Bei Schrittgeschwindigkeit will man die nächste Einmündung sehen, auf der
     * Landstraße den nächsten Kilometer. Eine feste Stufe kann nur eines von
     * beidem. Zwischen den Stützstellen wird linear gemischt.
     *
     * Die Werte gelten nur für die Navigationsansicht. In der Kolonnenansicht
     * bestimmt der Abstand zu den anderen, was zu sehen ist — nicht das Tempo.
     *
     * Beide Stützstellen sind Vielfache von `navigationZoomStep`. Sonst könnte
     * die gerundete Rechnung sie nie treffen, und die Konfiguration behauptete
     * einen Wert, den es nicht gibt.
     */
    navigationZoomSlowKmh: 30,
    navigationZoomFastKmh: 100,
    navigationZoomClose: 16.25,
    navigationZoomFar: 14.25,
    /**
     * Stufung der Vergrößerung.
     *
     * Ohne sie folgte die Karte jedem Zeigerausschlag des Tachos und atmete
     * unablässig. Auf ein Viertel gerundet bleibt sie ruhig und ändert sich nur,
     * wenn sich das Tempo wirklich geändert hat.
     */
    navigationZoomStep: 0.25,
});
/**
 * Ab wann eine fremde Position als veraltet gekennzeichnet wird.
 *
 * Das ist keine Schönheitsfrage: Eine veraltete Position darf niemals wie eine
 * aktuelle dargestellt werden. Erreicht ein Fahrzeug die zweite Stufe, wird es
 * ausgegraut und mit dem Alter beschriftet.
 */
export const STALENESS = Object.freeze({
    /** Darüber wird die weiche Fortschreibung der Bewegung eingestellt. */
    extrapolationStopMs: 5_000,
    /**
     * Darüber gilt die Position als veraltet und wird als solche gekennzeichnet.
     *
     * Dreißig Sekunden, nicht fünfzehn. Ein **stehendes** Fahrzeug meldet
     * planmäßig nur alle zehn Sekunden (positionIntervalIdleMs); dazu kommen
     * Bündelung und Übertragung. Mit fünfzehn Sekunden rutschte eine
     * kerngesunde stehende Kolonne regelmäßig ins Ausgegraute — und ein
     * Hinweis, der im Normalfall angeht, wird nicht mehr gelesen.
     *
     * Nach oben ist die Grenze der Zeitablauf für „kein Signal" (60 s): Bis
     * dahin muss deutlich gewesen sein, dass die Anzeige nicht mehr frisch ist.
     */
    staleMs: 30_000,
    /**
     * Wie frisch die Meldung der Führung sein muss, damit „Abseits" überhaupt in
     * Betracht kommt.
     *
     * Die Erkennung misst den Abstand zur Führungsspur. Wächst die Spur nicht
     * mehr — weil die Führung im Funkloch steckt oder ihre App im Hintergrund
     * liegt —, entfernt sich **jeder**, der weiterfährt, von ihrem letzten
     * bekannten Punkt. Und zwar mit seiner Fahrgeschwindigkeit: Bei 78 km/h sind
     * die 150 m der Schwelle nach sieben Sekunden erreicht, bei 100 km/h nach
     * fünfeinhalb.
     *
     * Genau das ist am 17.08.2026 um 15:05 geschehen. Die Führung meldete
     * dreizehn Sekunden lang nichts, die Kolonne fuhr mit 78 km/h weiter, und
     * zwei Fahrzeuge mitten auf der Strecke bekamen „Abseits der Strecke"
     * gemeldet. In den Daten wächst ihr Abstand um genau 17 Meter je Sekunde —
     * ihre eigene Geschwindigkeit.
     *
     * Fünf Sekunden: Im Fahrbetrieb meldet ein Gerät jede Sekunde, die Spur ist
     * also nie älter als ein, zwei Sekunden. Steht die Führung, meldet sie im
     * langsamen Takt alle zehn Sekunden — dann gilt die Spur als nicht aktuell
     * und es wird nicht **neu** auf „abseits" erkannt.
     *
     * Hier stand einmal: „Das ist kein Verlust — wer vor einer stehenden Führung
     * abbiegt, wird gemeldet, sobald sie wieder fährt." Das galt für den Fall, an
     * den dabei gedacht war. Nicht bedacht war der andere: dass die Führung
     * **deshalb** steht, weil jemand abseits ist. Am 27.08.2026 wechselte sein
     * Abzeichen in der Kolonnenliste dadurch im Fünfsekundentakt zwischen
     * „Abseits" und „Anschluss" — genau in der Lage, in der die Angabe am
     * meisten zählt.
     *
     * Seither hält `determineState` den zuletzt festgestellten Zustand, solange
     * die Spur schweigt (A-66). Diese Grenze bleibt davon unberührt: Sie
     * verhindert weiterhin, dass jemand **neu** zum Falschabbieger erklärt wird,
     * nur weil die Führung gerade nichts meldet.
     */
    /*
     * Obergrenze, nicht die Frist selbst (A-73).
     *
     * Tatsächlich gilt die kürzere von beidem: dieser Frist und der Zeit, die das
     * Fahrzeug bei seinem eigenen Tempo für `offRouteDistanceM` braucht. Fünf
     * Sekunden und 150 Meter passen bis 30 m/s — darüber wäre die Schwelle
     * gefahren, bevor die Frist abläuft. Siehe `determineState`.
     */
    trackFreshForOffRouteMs: 5_000,
});
//# sourceMappingURL=timing.js.map